import { useMemo } from 'react';
import { useCurrentUser } from './current-user/current-user';

/**
 * Hook to access feature flags for the current user.
 *
 * @returns {Record<string, boolean | unknown>} An object containing feature flags
 *
 * @remarks
 * - Feature flags are sourced from the user's company `ld_feature_flags` property
 * - In development, flags can be overridden using the `VITE_FEATURE_FLAGS` environment variable
 * - The format for development overrides is: "flag1:true;flag2:false"
 * - Note that unlike the LaunchDarkly SDK, the flags in `ld_feature_flags` remain in snake_case
 *   (e.g., "my_feature_flag" instead of "myFeatureFlag")
 *
 * @example
 * ```tsx
 * const { my_feature_flag } = useFeatureFlag();
 * if (my_feature_flag) {
 *   // Feature is enabled
 * }
 * ```
 */
export const useFeatureFlag = (): Record<string, boolean | unknown> => {
  const user = useCurrentUser();

  return useMemo(() => {
    const features = user.company.ld_feature_flags ?? {};

    if (
      process.env.NODE_ENV === 'development' &&
      process.env.VITE_FEATURE_FLAGS
    ) {
      const overrides = Object.fromEntries(
        (process.env.VITE_FEATURE_FLAGS ?? '')
          .split(';')
          .map((flag: string) => flag.split(':'))
          .map(([flag, value]: string[]) => [flag, value === 'true']),
      );

      return { ...features, ...overrides };
    }
    return features;
  }, [user]);
};
